/**
 * Shared state and helpers for v2 onboarding telemetry.
 *
 * The flow session id lives in sessionStorage so events emitted before and
 * after an OAuth redirect (same tab) can be joined into one funnel run; the
 * started flag (keyed to the flow id) lets a remount emit onboarding_resumed
 * instead of a duplicate onboarding_started. Used by OnboardingTelemetry
 * (passive listeners) and BusinessSignupV2 (launch outcome events, which
 * never surface in the URL or the DOM).
 */
import { trackEvent } from '@/lib/posthog';

export const FLOW_VERSION = 'magic-onboarding-v2';

const FLOW_SESSION_STORAGE_KEY = 'figgy_onboarding_flow_session_id';
const FLOW_STARTED_STORAGE_KEY = 'figgy_onboarding_started_sent';

export const STEP_KEYS: Record<number, string> = {
  1: 'account',
  2: 'business_details',
  3: 'services',
  4: 'schedule',
  5: 'branding',
};

const LAUNCH_STEP_NUMBER = 5;

function createFlowSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getFlowSessionId(): string {
  try {
    const existing = sessionStorage.getItem(FLOW_SESSION_STORAGE_KEY);
    if (existing) return existing;

    const created = createFlowSessionId();
    sessionStorage.setItem(FLOW_SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return createFlowSessionId();
  }
}

/** True when onboarding_started was already sent for the current flow id. */
export function hasFlowStarted(): boolean {
  try {
    return sessionStorage.getItem(FLOW_STARTED_STORAGE_KEY) === getFlowSessionId();
  } catch {
    return false;
  }
}

export function markFlowStarted(): void {
  try {
    sessionStorage.setItem(FLOW_STARTED_STORAGE_KEY, getFlowSessionId());
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

/** Forget the flow id and started flag; the next visit starts a fresh flow. */
export function clearFlowState(): void {
  try {
    sessionStorage.removeItem(FLOW_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(FLOW_STARTED_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

// ── Step timing ───────────────────────────────────────────────────────────────
// Module-level so BusinessSignupV2 can report step-5 duration on launch
// without reaching into the telemetry component.

let stepStartedAt = Date.now();

export function markStepStarted(): void {
  stepStartedAt = Date.now();
}

export function stepElapsedMs(): number {
  return Date.now() - stepStartedAt;
}

// ── Field identification ──────────────────────────────────────────────────────

const AUTOCOMPLETE_FIELD_NAMES: Record<string, string> = {
  name: 'full_name',
  organization: 'business_name',
  'street-address': 'business_address',
  'address-line2': 'business_suite',
};

function sanitizeIdentifier(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

/**
 * Stable analytics name for a form control. data-onboarding-field is the
 * primary identifier; autocomplete/name/id are fallbacks so a field someone
 * forgets to tag still gets a best-effort label instead of a guess.
 */
export function resolveOnboardingFieldName(
  element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
): string {
  const explicit = element.getAttribute('data-onboarding-field');
  if (explicit) {
    const sanitized = sanitizeIdentifier(explicit);
    if (sanitized) return sanitized;
  }

  const autocomplete = element.getAttribute('autocomplete') ?? '';
  if (AUTOCOMPLETE_FIELD_NAMES[autocomplete]) return AUTOCOMPLETE_FIELD_NAMES[autocomplete];

  const namedIdentifier = element.getAttribute('name') || element.id;
  if (namedIdentifier) {
    const sanitized = sanitizeIdentifier(namedIdentifier);
    if (sanitized) return sanitized;
  }

  return `${element.tagName.toLowerCase()}_unnamed`;
}

// ── Error sanitization ────────────────────────────────────────────────────────
// Raw error messages can embed URLs, emails, and user-entered values, so they
// are never sent. Only an allowlisted name, a category, and structured
// status/code fields (validated to be pattern-safe) leave the device.

const KNOWN_ERROR_NAMES = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'ReferenceError',
  'EvalError',
  'URIError',
  'DOMException',
  'AbortError',
  'TimeoutError',
  'NetworkError',
  'SecurityError',
  'QuotaExceededError',
  'NotAllowedError',
  'AuthError',
  'AuthApiError',
  'AuthRetryableFetchError',
  'AuthSessionMissingError',
  'AuthWeakPasswordError',
  'PostgrestError',
  'StorageError',
  'StorageApiError',
  'StorageUnknownError',
  'FunctionsError',
  'FunctionsHttpError',
  'FunctionsRelayError',
  'FunctionsFetchError',
]);

const SAFE_CODE_PATTERN = /^[a-zA-Z0-9_-]{1,40}$/;

function categorizeError(name: string, message: string): string {
  if (name.startsWith('Auth')) return 'auth';
  if (name.startsWith('Postgrest')) return 'database';
  if (name.startsWith('Storage') || name === 'QuotaExceededError') return 'storage';
  if (name.startsWith('Functions')) return 'edge_function';
  if (name === 'AbortError') return 'aborted';
  if (name === 'TimeoutError') return 'timeout';
  if (name === 'NetworkError') return 'network';
  // Inspected locally to pick a category; the message itself is never sent.
  if (name === 'TypeError' && /fetch|network|load failed/i.test(message)) return 'network';
  if (KNOWN_ERROR_NAMES.has(name)) return 'runtime';
  return 'unknown';
}

export interface SanitizedErrorProperties {
  error_name: string;
  error_category: string;
  error_status?: number;
  error_code?: string;
}

export function sanitizedErrorProperties(error: unknown): SanitizedErrorProperties {
  if (error instanceof Error) {
    const properties: SanitizedErrorProperties = {
      error_name: KNOWN_ERROR_NAMES.has(error.name) ? error.name : 'Error',
      error_category: categorizeError(error.name, error.message),
    };

    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number' && Number.isFinite(status)) {
      properties.error_status = status;
    }

    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && SAFE_CODE_PATTERN.test(code)) {
      properties.error_code = code;
    }

    return properties;
  }

  return { error_name: 'UnknownError', error_category: 'unknown' };
}

// ── Launch outcome ────────────────────────────────────────────────────────────
// The DONE step never reaches the URL, so the telemetry wrapper cannot see
// the launch outcome; BusinessSignupV2 reports it through these.

function launchProperties(userId: string | undefined) {
  return {
    flow_version: FLOW_VERSION,
    flow_session_id: getFlowSessionId(),
    user_id: userId,
    step_number: LAUNCH_STEP_NUMBER,
    step_key: STEP_KEYS[LAUNCH_STEP_NUMBER],
  };
}

export function recordLaunchSucceeded(userId: string | undefined): void {
  const properties = launchProperties(userId);

  trackEvent('onboarding_step_completed', {
    ...properties,
    step_duration_ms: stepElapsedMs(),
    completion_action: 'launch',
  });
  trackEvent('onboarding_completed', properties);
  clearFlowState();
}

export function recordLaunchFailed(userId: string | undefined, error: unknown): void {
  trackEvent('onboarding_error', {
    ...launchProperties(userId),
    error_source: 'launch_save',
    ...sanitizedErrorProperties(error),
  });
}
